import React, { createContext, useContext, useState, useEffect, useMemo, useCallback, ReactNode } from 'react';
import { usePermissions } from './PermissionContext';
import { TenantPermission } from '../types/permissions';
import api from '../services/api';

interface TenantContextType {
  selectedTenant: TenantPermission | null;
  availableTenants: TenantPermission[];
  setSelectedTenant: (tenant: TenantPermission | null) => void;
  loading: boolean;
  error: string | null;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};

interface TenantProviderProps {
  children: ReactNode;
}

export const TenantProvider: React.FC<TenantProviderProps> = ({ children }) => {
  const { permissions, loading: permissionsLoading } = usePermissions();
  const [selectedTenant, setSelectedTenant] = useState<TenantPermission | null>(null);
  const [loading, setLoading] = useState(true);

  // Get available tenants from user permissions
  const availableTenants = useMemo(() => permissions?.tenant_permissions || [], [permissions?.tenant_permissions]);

  const handleSetSelectedTenant = useCallback(async (tenant: TenantPermission | null) => {
    const previousTenantId = selectedTenant?.tenant_id ?? null;
    const newTenantId = tenant?.tenant_id ?? null;

    if (tenant && newTenantId !== previousTenantId) {
      try {
        await api.setTenantSession(tenant.tenant_id, previousTenantId || undefined);
      } catch (err) {
        console.error('Failed to set tenant session (audit):', err);
        // Continue to set local state so UI still works
      }
    }

    setSelectedTenant(tenant);
    if (tenant) {
      sessionStorage.setItem('selectedTenant', JSON.stringify(tenant));
    } else {
      sessionStorage.removeItem('selectedTenant');
    }
  }, [selectedTenant?.tenant_id]);

  useEffect(() => {
    if (!permissionsLoading) {
      setLoading(false);

      // Auto-select first tenant only when user has exactly one tenant
      if (!selectedTenant && availableTenants.length === 1) {
        handleSetSelectedTenant(availableTenants[0]);
      }
    }
  }, [permissionsLoading, permissions, selectedTenant, availableTenants, handleSetSelectedTenant]);

  // Load selected tenant from sessionStorage on mount
  useEffect(() => {
    const storedTenant = sessionStorage.getItem('selectedTenant');
    if (storedTenant) {
      try {
        const tenant = JSON.parse(storedTenant);
        // Verify the tenant is still in available tenants
        if (availableTenants.some(t => t.tenant_id === tenant.tenant_id)) {
          setSelectedTenant(tenant);
        }
      } catch (error) {
        console.error('Failed to parse stored tenant:', error);
        sessionStorage.removeItem('selectedTenant');
      }
    }
  }, [availableTenants]);

  const value: TenantContextType = useMemo(() => ({
    selectedTenant,
    availableTenants,
    setSelectedTenant: handleSetSelectedTenant,
    loading,
    error: null,
  }), [selectedTenant, availableTenants, handleSetSelectedTenant, loading]);

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
}; 